(() => {
  const FOCUSABLE = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
    'summary',
  ].join(',');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const getFocusable = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
      if (el.closest('[inert]')) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });

  const refreshInertTargets = () => {
    window.inertElems = document.querySelectorAll('[data-js-inert]');
  };

  const announce = (message) => {
    const live = document.getElementById('screen-reader-info');
    if (!live || !message) return;
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = message;
    }, 50);
  };

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const nodes = getFocusable(container);
    if (!nodes.length) {
      event.preventDefault();
      container.focus();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const bindDialog = (dialog) => {
    if (!dialog || dialog.dataset.a11yBound === 'true') return;
    dialog.dataset.a11yBound = 'true';

    if (!dialog.hasAttribute('role')) dialog.setAttribute('role', 'dialog');
    if (!dialog.hasAttribute('aria-modal')) dialog.setAttribute('aria-modal', 'true');

    const onKeydown = (event) => {
      if (!dialog.classList.contains('active') && dialog.style.display === 'none') return;
      if (dialog.style.display === 'none' && !dialog.classList.contains('active')) return;
      if (!dialog.classList.contains('active')) return;
      trapFocus(dialog, event);
    };

    dialog.addEventListener('keydown', onKeydown);
  };

  const enhanceExternalLinks = () => {
    const label = window.KROWN?.settings?.locales?.opens_new_window || ' (opens in a new tab)';
    document.querySelectorAll('a[target="_blank"]').forEach((link) => {
      const rel = (link.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (!rel.includes('noopener')) rel.push('noopener');
      if (!rel.includes('noreferrer')) rel.push('noreferrer');
      link.setAttribute('rel', rel.join(' '));

      const existing = link.getAttribute('aria-label');
      if (existing && !/new (tab|window)/i.test(existing)) {
        link.setAttribute('aria-label', `${existing}${label}`);
      } else if (!existing && !link.textContent.trim()) {
        link.setAttribute('aria-label', label.trim());
      }
    });
  };

  const enhanceImages = () => {
    document.querySelectorAll('img:not([alt])').forEach((img) => {
      img.setAttribute('alt', '');
    });
  };

  const enhanceUnlabeledControls = () => {
    document.querySelectorAll('input[type="email"]:not([aria-label]):not([aria-labelledby])').forEach((input) => {
      const id = input.id;
      if (id && document.querySelector(`label[for="${id}"]`)) return;
      input.setAttribute('aria-label', window.KROWN?.settings?.locales?.email || 'Email');
    });

    document.querySelectorAll('button.thumbnail:not([aria-label])').forEach((button, index) => {
      const alt = button.getAttribute('data-alt');
      button.setAttribute('aria-label', alt ? `View image ${index + 1}: ${alt}` : `View image ${index + 1}`);
    });

    document.querySelectorAll('.basicLightboxClose').forEach((close) => {
      if (!close.getAttribute('aria-label')) {
        close.setAttribute('aria-label', window.KROWN?.settings?.locales?.close || 'Close');
      }
      if (close.tagName !== 'BUTTON' && !close.hasAttribute('tabindex')) {
        close.setAttribute('role', 'button');
        close.setAttribute('tabindex', '0');
      }
    });

    const main = document.getElementById('main');
    if (main && !document.querySelector('h1')) {
      const heading = document.createElement('h1');
      heading.className = 'visually-hidden';
      heading.textContent = document.title.split(/[-–—|]/)[0].trim() || 'Home';
      main.prepend(heading);
    }
  };

  const demoteDuplicateProductTitles = () => {
    document.querySelectorAll('.product-header--mobile h1').forEach((heading) => {
      const replacement = document.createElement('p');
      replacement.className = heading.className;
      replacement.innerHTML = heading.innerHTML;
      heading.replaceWith(replacement);
    });
  };

  const enhanceSliderButtons = () => {
    document.querySelectorAll('.css-slider-button, .announcement-bar__content-nav, [role="button"]').forEach((el) => {
      if (el.tagName === 'BUTTON' || el.tagName === 'A') return;
      if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      if (el.dataset.a11yKeyBound === 'true') return;
      el.dataset.a11yKeyBound = 'true';
      el.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          el.click();
        }
      });
    });
  };

  const pauseAutoplayForReducedMotion = () => {
    if (!prefersReducedMotion.matches) return;
    document.querySelectorAll('css-slider').forEach((slider) => {
      if (typeof slider.pause === 'function') slider.pause();
      if (slider.o) slider.o.autoplay = 0;
    });
    document.querySelectorAll('[data-autoplay], video[autoplay]').forEach((media) => {
      if (typeof media.pause === 'function') media.pause();
    });
  };

  const enableYoutubeCaptions = () => {
    document.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtube-nocookie.com"]').forEach((iframe) => {
      if (iframe.src.includes('cc_load_policy')) return;
      iframe.src += (iframe.src.includes('?') ? '&' : '?') + 'cc_load_policy=1';
    });
  };

  const addMotionPauseControls = () => {
    const pauseLabel = window.KROWN?.settings?.locales?.pause_animation || 'Pause animation';
    const playLabel = window.KROWN?.settings?.locales?.play_animation || 'Play animation';
    const pauseVideo = window.KROWN?.settings?.locales?.pause_video || 'Pause video';
    const playVideo = window.KROWN?.settings?.locales?.play_video_media || 'Play video';

    const iconPause = '<svg class="a11y-pause-control__icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="6.5" y="5" width="3.5" height="14" rx="1"/><rect x="14" y="5" width="3.5" height="14" rx="1"/></svg>';
    const iconPlay = '<svg class="a11y-pause-control__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.2 5.4c0-.7.8-1.1 1.4-.7l10 6.1c.6.4.6 1.2 0 1.6l-10 6.1c-.6.4-1.4 0-1.4-.7V5.4z"/></svg>';

    const makeToggle = (pausedStart, onToggle, labels) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'a11y-pause-control';
      let paused = pausedStart;
      const sync = () => {
        button.innerHTML = paused ? iconPlay : iconPause;
        button.setAttribute('aria-label', paused ? labels.play : labels.pause);
        button.setAttribute('aria-pressed', paused ? 'true' : 'false');
        button.title = paused ? labels.play : labels.pause;
      };
      sync();
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        paused = !paused;
        onToggle(paused);
        sync();
      });
      return button;
    };

    document.querySelectorAll('video[autoplay]').forEach((video) => {
      if (video.closest('[data-a11y-pause-bound]') || video.hasAttribute('data-a11y-pause-bound')) return;
      if (video.closest('[data-reviews-reel]')) return;
      const wrap = video.closest('video-background-element, .slider-item-media-holder, .site-box-background') || video.parentElement;
      if (!wrap) return;
      wrap.setAttribute('data-a11y-pause-bound', 'true');
      if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
      wrap.appendChild(makeToggle(video.paused, (paused) => {
        if (paused) video.pause();
        else video.play();
      }, { pause: pauseVideo, play: playVideo }));
    });

    document.querySelectorAll('scrolling-text').forEach((scroller) => {
      if (scroller.dataset.a11yPauseBound === 'true') return;
      scroller.dataset.a11yPauseBound = 'true';
      if (!scroller._scroller) return;
      const host = scroller.parentElement || scroller;
      host.setAttribute('data-a11y-pause-bound', 'true');
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(makeToggle(false, (paused) => {
        if (paused) scroller._scroller.stop();
        else scroller._scroller.start(scroller._scrollerDirection);
      }, { pause: pauseLabel, play: playLabel }));
    });

    document.querySelectorAll('[data-replo-marquee-root]').forEach((marquee) => {
      if (marquee.dataset.a11yPauseBound === 'true') return;
      marquee.dataset.a11yPauseBound = 'true';
      if (getComputedStyle(marquee).position === 'static') marquee.style.position = 'relative';
      marquee.querySelectorAll('[data-replo-marquee-track]').forEach((track) => {
        const duration = getComputedStyle(track).animationDuration;
        if (!duration || duration === '0s') return;
        track.style.animationDuration = duration.split(',').map((part) => {
          const value = parseFloat(part);
          if (Number.isNaN(value)) return part.trim();
          return part.trim().endsWith('ms') ? `${value * 2.5}ms` : `${value * 2.5}s`;
        }).join(', ');
      });
      marquee.appendChild(makeToggle(false, (paused) => {
        marquee.classList.toggle('is-paused', paused);
      }, { pause: pauseLabel, play: playLabel }));
    });
  };

  const restoreTriggerOnHide = () => {
    const markTrigger = (handle) => {
      handle.addEventListener('click', () => {
        document.body.dataset.a11yLastTrigger = handle.id || '';
        handle.dataset.a11yLast = 'true';
      });
    };
    document.querySelectorAll('[data-js-sidebar-handle]').forEach(markTrigger);

    const originalHide = window.customElements.get('sidebar-drawer');
    if (!originalHide) return;
  };

  const patchSidebarFocus = () => {
    const proto = customElements.get('sidebar-drawer')?.prototype;
    if (!proto || proto.__a11yPatched) return;
    proto.__a11yPatched = true;

    const originalShow = proto.show;
    const originalHide = proto.hide;

    proto.show = function show() {
      this._a11yTrigger = document.activeElement;
      refreshInertTargets();
      originalShow.call(this);
      this.setAttribute('aria-hidden', 'false');
      window.setTimeout(() => {
        const close = this.querySelector('.site-close-handle, [data-js-first-focus], input, button, a[href]');
        (close || this).focus();
      }, 50);
    };

    proto.hide = function hide() {
      originalHide.call(this);
      this.setAttribute('aria-hidden', 'true');
      document.querySelectorAll('[aria-controls="' + this.id + '"]').forEach((handle) => {
        handle.setAttribute('aria-expanded', 'false');
      });
      if (this._a11yTrigger && typeof this._a11yTrigger.focus === 'function') {
        window.setTimeout(() => this._a11yTrigger.focus(), 60);
      }
    };
  };

  const patchModalFocus = () => {
    const proto = customElements.get('modal-box')?.prototype;
    if (!proto || proto.__a11yPatched) return;
    proto.__a11yPatched = true;

    const originalShow = proto.show;
    const originalHide = proto.hide;

    proto.show = function show() {
      this._a11yTrigger = document.activeElement;
      refreshInertTargets();
      originalShow.call(this);
      this.setAttribute('aria-hidden', 'false');
      window.setTimeout(() => {
        const first = this.querySelector('[data-js-first-focus], .site-close-handle, input, button, a[href]');
        (first || this).focus();
      }, 60);
    };

    proto.hide = function hide(remember) {
      originalHide.call(this, remember);
      this.setAttribute('aria-hidden', 'true');
      if (this._a11yTrigger && typeof this._a11yTrigger.focus === 'function') {
        window.setTimeout(() => this._a11yTrigger.focus(), 60);
      }
    };
  };

  const watchCustomElements = () => {
    if (customElements.get('sidebar-drawer')) patchSidebarFocus();
    else customElements.whenDefined('sidebar-drawer').then(patchSidebarFocus);

    if (customElements.get('modal-box')) patchModalFocus();
    else customElements.whenDefined('modal-box').then(patchModalFocus);
  };

  const dedupeJudgeMeReviews = () => {
    const widgets = document.querySelectorAll('.jdgm-widget.jdgm-review-widget');
    widgets.forEach((widget, index) => {
      if (index === 0) return;
      const section = widget.closest('.shopify-section');
      (section || widget).remove();
    });
  };

  const onReady = () => {
    refreshInertTargets();
    document.querySelectorAll('[role="dialog"], modal-box, sidebar-drawer').forEach(bindDialog);
    enhanceExternalLinks();
    enhanceImages();
    enhanceUnlabeledControls();
    enhanceSliderButtons();
    demoteDuplicateProductTitles();
    window.setTimeout(demoteDuplicateProductTitles, 200);
    window.setTimeout(demoteDuplicateProductTitles, 800);
    dedupeJudgeMeReviews();
    window.setTimeout(dedupeJudgeMeReviews, 200);
    window.setTimeout(dedupeJudgeMeReviews, 1200);
    pauseAutoplayForReducedMotion();
    enableYoutubeCaptions();
    window.setTimeout(addMotionPauseControls, 400);
    restoreTriggerOnHide();
    watchCustomElements();

    const main = document.getElementById('main');
    if (main && !main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');

    document.querySelectorAll('form').forEach((form) => {
      const firstError = form.querySelector('[aria-invalid="true"], .error, .alert--error');
      if (firstError) {
        const focusTarget = form.querySelector('[aria-invalid="true"], input.error, textarea.error');
        if (focusTarget) focusTarget.focus();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  window.a11yAnnounce = announce;
})();
